import { useState, useEffect, useCallback } from 'react';
import { User } from '../types';
import { supabase } from '../services/supabase';
import * as db from '../services/dataService';
import { CATEGORIES } from '../constants';

interface AuthModalConfig {
    requireCompletion: boolean;
    userToComplete: User | null;
}

interface UseAuthReturn {
    user: User | null;
    setUser: React.Dispatch<React.SetStateAction<User | null>>;
    showAuthModal: boolean;
    setShowAuthModal: React.Dispatch<React.SetStateAction<boolean>>;
    authModalConfig: AuthModalConfig;
    setAuthModalConfig: React.Dispatch<React.SetStateAction<AuthModalConfig>>;
    handleLoginSuccess: (loggedInUser: User) => void;
    handleLogout: () => Promise<void>;
    handleUpgradeSubscription: (tier: 'pro' | 'premium') => Promise<void>;
}

export const useAuth = (): UseAuthReturn => {
    const [user, setUser] = useState<User | null>(null);
    const [showAuthModal, setShowAuthModal] = useState(false);
    const [authModalConfig, setAuthModalConfig] = useState<AuthModalConfig>({
        requireCompletion: false,
        userToComplete: null
    });

    // Helper to process supabase user into App User
    const handleSessionUser = useCallback(async (sessionUser: any) => {
        let role: 'admin' | 'user' = 'user';
        let name = sessionUser.user_metadata?.full_name || sessionUser.email?.split('@')[0];
        let subscriptionTier: 'free' | 'pro' | 'premium' = 'free';
        let phone = undefined;
        let targetExam = undefined;
        let allowedCategories: string[] = [];
        const userEmail = sessionUser.email;

        console.log('[Auth] Processing session for:', userEmail, 'ID:', sessionUser.id);

        // 1. First Check Local Cache for Profile Details (Fast Fallback)
        const cachedProfile = db.getLocalUserProfile(sessionUser.id);
        if (cachedProfile) {
            if (cachedProfile.role === 'admin') role = 'admin';
            if (cachedProfile.phone) phone = cachedProfile.phone;
            if (cachedProfile.targetExam) targetExam = cachedProfile.targetExam;
            if (cachedProfile.name) name = cachedProfile.name;
            if (cachedProfile.allowedCategories) allowedCategories = cachedProfile.allowedCategories;
            console.log('[Auth] Loaded from cache - role:', cachedProfile.role);
        }

        // 2. Try to fetch profile from DB
        if (supabase) {
            let profileFound = false;

            // 2a. Try by USER ID first (primary lookup)
            try {
                const { data, error } = await supabase
                    .from('profiles')
                    .select('role, name, subscription_tier, phone, target_exam, allowed_categories')
                    .eq('id', sessionUser.id)
                    .single();

                console.log('[Auth] Supabase profile fetch by ID result:', { data, error: error?.message });

                if (data && !error) {
                    profileFound = true;
                    // Use case-insensitive comparison and trim whitespace for role check
                    const fetchedRole = (data.role || '').toString().trim().toLowerCase();
                    if (fetchedRole === 'admin') {
                        role = 'admin';
                        subscriptionTier = 'premium';
                    }
                    console.log('[Auth] Supabase role by ID:', JSON.stringify(data.role), '-> resolved as:', role);
                    if (data.name) name = data.name;
                    if (data.subscription_tier) subscriptionTier = role === 'admin' ? 'premium' : data.subscription_tier;
                    if (data.phone) phone = data.phone;
                    if (data.target_exam) targetExam = data.target_exam;
                    if (data.allowed_categories) allowedCategories = data.allowed_categories || [];
                }
            } catch (err) {
                console.error('[Auth] Error fetching profile by ID:', err);
            }

            // 2b. If ID lookup didn't find admin role, ALSO try by EMAIL
            // This handles cases where profile ID doesn't match auth user ID
            // (e.g., manually created profiles, OAuth/email account linking)
            if (role !== 'admin') {
                try {
                    const profileByEmail = await db.getProfileByEmail(userEmail);
                    console.log('[Auth] Email-based profile lookup result:', profileByEmail ? { role: profileByEmail.role, email: userEmail } : 'not found');
                    
                    if (profileByEmail) {
                        const emailRole = (profileByEmail.role || '').toString().trim().toLowerCase();
                        if (emailRole === 'admin') {
                            role = 'admin';
                            subscriptionTier = 'premium';
                            console.log('[Auth] Admin role detected via email lookup!');
                        }
                        // If ID lookup didn't find a profile, also use other fields from email lookup
                        if (!profileFound) {
                            if (profileByEmail.name) name = profileByEmail.name;
                            if (profileByEmail.subscriptionTier && role !== 'admin') subscriptionTier = profileByEmail.subscriptionTier;
                            if (profileByEmail.phone) phone = profileByEmail.phone;
                            if (profileByEmail.targetExam) targetExam = profileByEmail.targetExam;
                            if (profileByEmail.allowedCategories) allowedCategories = profileByEmail.allowedCategories;
                        }

                        // Ensure the auth user's profile row exists with correct data
                        if (!profileFound) {
                            await db.updateUserProfile(sessionUser.id, {
                                name,
                                phone,
                                targetExam,
                                email: userEmail,
                                allowedCategories
                            });
                        }
                    } else if (!profileFound) {
                        console.log('[Auth] No profile exists at all, creating new one');
                        await db.updateUserProfile(sessionUser.id, {
                            name,
                            email: userEmail
                        });
                    }
                } catch (err) {
                    console.error('[Auth] Error in email-based profile lookup:', err);
                }
            }
        } else {
            console.log('[Auth] Supabase client is null - running in local/mock mode');
        }

        // 5. Fallback: Check email against known admin list
        // This ensures admin access even if Supabase profile query has issues
        const ADMIN_EMAILS = [
            'admin@thrusted.in',
            'thrusted.contact@gmail.com',
            'bhanutejav99@gmail.com',
            'yasmin.achukatla26@gmail.com'
        ];
        if (role !== 'admin' && userEmail && ADMIN_EMAILS.includes(userEmail.toLowerCase())) {
            role = 'admin';
            subscriptionTier = 'premium';
            console.log('[Auth] Admin role set via hardcoded email list for:', userEmail);
        }

        // If user is non-admin and allowedCategories is empty but targetExam is defined, map it!
        if (role === 'user' && allowedCategories.length === 0 && targetExam) {
            const matchedCategory = CATEGORIES.find(
                c => c.name.toLowerCase() === targetExam.toLowerCase() || c.id.toLowerCase() === targetExam.toLowerCase()
            );
            if (matchedCategory) {
                allowedCategories = [matchedCategory.id];
            }
        }

        // Update Cache with fresh data (include role so it persists locally)
        db.updateLocalProfileCache(sessionUser.id, { name, phone, targetExam, email: userEmail, allowedCategories, role });
        console.log('[Auth] Final resolved user:', { email: userEmail, role, subscriptionTier, allowedCategories });

        const appUser: User = {
            id: sessionUser.id,
            email: sessionUser.email!,
            role: role,
            name: name,
            subscriptionTier: subscriptionTier,
            phone: phone,
            targetExam: targetExam,
            allowedCategories: allowedCategories
        };

        setUser(appUser);

        // 6. Check if profile is incomplete (e.g. from Google Login)
        if (!phone || !targetExam) {
            setAuthModalConfig({ requireCompletion: true, userToComplete: appUser });
            setShowAuthModal(true);
        } else {
            setAuthModalConfig({ requireCompletion: false, userToComplete: null });
        }
    }, []);

    // Initial Auth Setup & Subscription
    useEffect(() => {
        if (supabase) {
            // 1. Get initial session
            supabase.auth.getSession().then(({ data: { session } }) => {
                if (session?.user) {
                    handleSessionUser(session.user);
                }
            });

            // 2. Listen for auth changes (Signed In, Signed Out, OAuth Redirects)
            const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
                if (session?.user) {
                    handleSessionUser(session.user);
                } else {
                    setUser(null);
                }
            });

            return () => {
                subscription.unsubscribe();
            };
        }
    }, [handleSessionUser]);

    const handleLoginSuccess = useCallback((loggedInUser: User) => {
        setUser(loggedInUser);
        setShowAuthModal(false);
        setAuthModalConfig({ requireCompletion: false, userToComplete: null });
    }, []);

    const handleLogout = useCallback(async () => {
        if (supabase) await supabase.auth.signOut();
        setUser(null);
    }, []);

    const handleUpgradeSubscription = useCallback(async (tier: 'pro' | 'premium') => {
        if (!user) return;

        const success = await db.upgradeUserSubscription(user.id, tier);
        if (success) {
            setUser(prev => prev ? { ...prev, subscriptionTier: tier } : null);
        }
    }, [user]);

    return {
        user,
        setUser,
        showAuthModal,
        setShowAuthModal,
        authModalConfig,
        setAuthModalConfig,
        handleLoginSuccess,
        handleLogout,
        handleUpgradeSubscription
    };
};

export default useAuth;
